import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { SortAndFiltersViewType } from '@shared-ui/search-tools-lodging';
import { useClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { ViewExtraLarge, ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { SearchResultsStore } from 'stores/search-results-store';
import { useViewTrackerContext } from 'src/common/stores/contexts/viewTracker';

import { ResultType } from 'constants/resultType';
import { SearchFormFields } from 'src/common/models';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';

import { FiltersToggle } from 'components/search/sort-filter/filters-toggle';
import { MapPreview } from 'src/common/components/search/map/map-preview';
import { ResultHeaderFilterPills } from './result-header-filter-pills';
import { ResultsSubHeader } from './results-sub-header';
import { UniversalSortFilterWrapper } from './sort-filter/universal-sort-filter';

export interface ResultsHeaderProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}

export interface ResultsHeaderState {
  resultsHeaderActive: boolean;
}

const OVERFILTERED_LISTING_MAX_POSITION = 10;

@withLocalization
@inject('searchResults')
@observer
export class ResultsHeader extends React.Component<ResultsHeaderProps, ResultsHeaderState> {
  private searchResultsHeaderWrapper;

  public static defaultProps = {
    l10n: null,
    withMapButton: false,
    withFilterButton: false,
  };

  constructor(props: ResultsHeaderProps) {
    super(props);

    this.state = { resultsHeaderActive: false };
    this.searchResultsHeaderWrapper = React.createRef();
  }

  public componentDidMount() {
    this.setState({
      resultsHeaderActive: true,
    });
  }

  public render() {
    const { l10n, searchResults, withMapButton, withFilterButton } = this.props;

    if (!searchResults) {
      return null;
    }

    const { isLoading, loyaltyMessagingModel, searchCriteria, sortAndFilter, resultsSummary, resultMessages } = searchResults;
    const pills = sortAndFilter && sortAndFilter.pills;
    const filters = searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.filters;
    const ResultsSubHeaderProps = {
      resultsSummary,
      resultMessages,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria,
    };
    const buttonsProps = {
      isActive: this.state.resultsHeaderActive,
      filters,
      dialogTitle: l10n.formatText('hotels.searchResults.filtersDialog.title'),
      withMapButton,
      withFilterButton,
    };

    const isOverFiltered = searchResults?.propertySearchListings
      ?.slice(0, OVERFILTERED_LISTING_MAX_POSITION)
      .some(listing => listing.resultType === ResultType.PROPERTY_SEARCH_OVERFILTERED_CARD);

    const shouldRenderPills = pills && pills.length > 0 && !isOverFiltered;

    return (
      <ErrorBoundary label="hotel.search.tools">
        <UitkSpacing margin={{ blockstart: 'one' }}>
          <section data-stid="results-header" ref={this.searchResultsHeaderWrapper.value}>
            <ButtonsParent {...buttonsProps} />
            {shouldRenderPills && <ResultHeaderFilterPills />}
            <ResultsSubHeader {...ResultsSubHeaderProps} />
          </section>
        </UitkSpacing>
      </ErrorBoundary>
    );
  }
}

const Buttons = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton }: ButtonsProps) => {
  const viewTracker = useViewTrackerContext();
  const tracker = useClickstreamAnalytics();

  return (
    <UitkSpacing data-stid="tools__actions">
      <UitkLayoutFlex>
        {withFilterButton && (
          <UitkLayoutFlexItem grow={2} data-stid="filters-toggle-button">
            <UitkSpacing margin={{ block: 'one' }} padding={{ blockend: 'three' }}>
              <div>
                <Experiment name="Universal_Sort_and_Filter_Lodging">
                  <ExperimentControl>
                    <FiltersToggle
                      buttonComponent={UitkSecondaryButton}
                      isActive={isActive}
                      isFullWidth={true}
                      label="hotels.searchResults.openFiltersDialog.label"
                      a11yMessage="hotels.searchResults.appliedFiltersCount.a11yLabel"
                      rfrr=":FilterControl"
                      stid="open-hotel-filters"
                      filters={filters}
                      dialogParam="filter"
                      dialogTitle={dialogTitle}
                      showSeparator={true}
                    />
                  </ExperimentControl>
                  <ExperimentVariant bucket={1}>
                    <UitkLayoutFlex>
                      <UitkLayoutFlexItem grow={2}>
                        <UniversalSortFilterWrapper view={SortAndFiltersViewType.SORT_AND_FILTERS} />
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </ExperimentVariant>
                </Experiment>
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        )}
        {withMapButton && (
          <UitkLayoutFlexItem grow={1} data-stid="map-preview-button">
            <UitkSpacing margin={'one'}>
              <div>
                <MapPreview viewTracker={viewTracker} tracker={tracker} />
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
    </UitkSpacing>
  );
};

interface ButtonsProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  classes?: string;
}

const ButtonsParent = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton }: ButtonsParentProps) => (
  <Experiment name="lodging_reconnect_travel_hackathon">
    <ExperimentControl>
      <Viewport>
        <ViewSmall>
          <Buttons isActive={isActive} filters={filters} dialogTitle={dialogTitle} withFilterButton={true} withMapButton={true} />
        </ViewSmall>
        <ViewLarge>
          <></>
        </ViewLarge>
        <ViewExtraLarge>
          <Buttons
            isActive={isActive}
            filters={filters}
            dialogTitle={dialogTitle}
            withMapButton={withMapButton}
            withFilterButton={withFilterButton}
          />
        </ViewExtraLarge>
      </Viewport>
    </ExperimentControl>
    <ExperimentVariant bucket={1}>
      <Buttons isActive={isActive} filters={filters} dialogTitle={dialogTitle} withFilterButton={true} withMapButton={true} />
    </ExperimentVariant>
  </Experiment>
);

interface ButtonsParentProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}
